import { ApiClient } from "@/utils/apiClient";
import type { ApiResponse } from "@/models/api";
import type { User } from "@/models/user";
import type { Membership } from "@/models/membership";
import type { PasswordResetToken } from "@/models/passwordResetToken";
import { getUTMParamsAsHeaders } from "../utils/utm";

export type SignUpPayload = {
  firstName?: string;
  lastName?: string;
  email: string;
  password: string;
  passwordConfirmation?: string;
  newsletter?: boolean;
}

export type SignUpResponse = ApiResponse<{
  user: User;
  membership: Membership;
}>;

export type LoginPayload = {
  email: string;
  password: string;
}

export type LoginResponse = ApiResponse<{
  user: User;
  membership: Membership;
  lotteryTicketNumbers: string[];
}>;

export type ResetPasswordBeginPayload = {
  email: string;
}

export type ResetPasswordValidateTokenPayload = {
  email: string;
  token: string;
};

export type ResetPasswordValidateTokenResponse = ApiResponse<{
  token?: PasswordResetToken;
}>;

export type ResetPasswordPayload = {
  email: string;
  token: string;
  password: string;
  passwordConfirmation: string;
};

export type ResetPasswordResponse = ApiResponse<{
  user: User;
}>;

export type SocialiteRedirectPayload = {
  client: string;
  page: string;
  referralCode?: string;
}

export type SocialiteRedirectResponse = ApiResponse<{
  targetUrl: string;
}>;

export abstract class AuthApiService {
  public static async setCsrfTokenCookie(): Promise<void> {
    // Automatically sets the cookie to store the token
    await ApiClient.getClient({webScoped: false, clientScoped: false}).get('/sanctum/csrf-cookie');
  }

  // TODO client scoped temporarily
  public static async signUp(payload: SignUpPayload): Promise<SignUpResponse> {
    const response = await ApiClient.getClient().post<SignUpResponse>(
      '/signup',
      payload,
    );
    return response.data;
  }

  public static async login(payload: LoginPayload): Promise<LoginResponse> {
    const response = await ApiClient.getClient().post<LoginResponse>('/login', payload);
    return response.data;
  }

  public static async logout(): Promise<void> {
    const response = await ApiClient.getClient().post('/logout');
    return response.data;
  }

  public static async resetPasswordBegin(payload: ResetPasswordBeginPayload): Promise<void> {
    await ApiClient.getClient().post('password-reset-begin', payload);
  }

  public static async resetPasswordValidateToken(payload: ResetPasswordValidateTokenPayload): Promise<ResetPasswordValidateTokenResponse> {
    const response = await ApiClient.getClient().post('password-reset-validate-token', payload);
    return response.data;
  }

  public static async resetPassword(payload: ResetPasswordPayload): Promise<ResetPasswordResponse> {
    const response = await ApiClient.getClient().post('password-reset', payload);
    return response.data;
  }

  public static async loginWithProvider(provider: string, payload: SocialiteRedirectPayload): Promise<SocialiteRedirectResponse> {
    const response = await ApiClient.getClient({clientScoped: false}).post(`/socialite/${provider}/redirect`, payload);
    return response.data;
  }
}
